// File: src/pages/reveal/RevealPortal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  getRevealSocket,
  startRevealAccessWatch,
  stopRevealAccessWatch,
  startLocationHeartbeat,
  stopLocationHeartbeat,
} from "../../utils/revealSocket";
import { enforceRevealState } from '../../utils/revealEnforcer';

import EndOfEventFeedbackModal from "../../components/EndOfEventFeedbackModal";
import PreRevealCard from "../../components/Reveal/PreRevealCard";
// ⬇️ Reveal UI components (match mixer portal layout)
import RevealPortalNavBar from "../../components/Reveal/RevealPortalNavBar";
import RevealInstructionsBanner from "../../components/Reveal/RevealInstructionsBanner";
import RevealEventDetailsCard from "../../components/Reveal/RevealEventDetailsCard";
import RevealHostCard from "../../components/Reveal/RevealHostCard";
import MinimalFooter from "../../components/MinimalFooter";

// NEW: intro reveal sequence overlay
import RevealIntroSequence from "../../components/Reveal/RevealIntroSequence";

/* helpers */
function authHeaders() {
  // Prefer active access token; fallback to legacy tribe_auth
  const primary = localStorage.getItem("accessToken") || "";
  if (primary) return { headers: { Authorization: `Bearer ${primary}` } };
  const ctx = JSON.parse(localStorage.getItem("tribe_auth") || "{}");
  const token = ctx?.token || "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function getUid() {
  const ctx = JSON.parse(localStorage.getItem("tribe_auth") || "{}");
  return (
    ctx?.uid ||
    ctx?.userId ||
    ctx?.user?._id ||
    localStorage.getItem("userId") ||
    ""
  );
}

function toLocalDateTime(eventDate, time12h) {
  if (!eventDate || !time12h) return null;
  const [y, m, d] = eventDate.split("-").map(Number);
  const [clock, ampm] = String(time12h).trim().split(" ");
  if (!clock || !ampm) return null;
  let [hh, mi] = clock.split(":").map(Number);
  if (ampm.toUpperCase() === "PM" && hh < 12) hh += 12;
  if (ampm.toUpperCase() === "AM" && hh === 12) hh = 0;
  return new Date(y, m - 1, d, hh, mi || 0, 0, 0);
}

function savePartnerPreload(payload) {
  try { sessionStorage.setItem("reveal_preload_match", JSON.stringify(payload)); } catch {}
}
function clearPartnerPreload() {
  try { sessionStorage.removeItem("reveal_preload_match"); } catch {}
}


export default function RevealPortal() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const api = process.env.REACT_APP_API_URL;
  
  useEffect(() => {
    enforceRevealState({ api, eventId, pageKind: 'portal', navigate });
  }, [api, eventId, navigate]);
 
  const [event, setEvent] = useState(null);

  const [currentFirstName, setCurrentFirstName] = useState("");
  useEffect(() => {
    const uid = getUid();
    if (!uid) return;
    (async () => {
      try {
        const { data } = await axios.get(`${api}/users/${uid}`, authHeaders());
        const fn =
          data?.firstName ??
          data?.first_name ??
          (typeof data?.name === "string" ? data.name.split(" ")[0] : "") ??
          "";
        if (fn) setCurrentFirstName(fn);
      } catch (e) {
        console.warn("[RevealPortal] fetch user failed", e?.response?.status, e?.message);
      }
    })();
  }, [api]);

  const [currentEpoch, setCurrentEpoch] = useState(null);
  const [until, setUntil] = useState(0);
  const [revealed, setRevealed] = useState(false);

  // NEW: keep PreRevealCard mounted during async intro prep + route handoff
  const [introBooting, setIntroBooting] = useState(false);
  const [navigating, setNavigating] = useState(false);

  // Round end & feedback
  const [roundEndAt, setRoundEndAt] = useState(null); // number (ms)
  const [showFeedback, setShowFeedback] = useState(false);

  // Intro overlay state (runs when reveal lands)
  const [showIntro, setShowIntro] = useState(false);
  const [introPartners, setIntroPartners] = useState([]); // [{firstName,lastName,age,gender,profilePicUrl}, ...]

  // prevent feedback modal flash until event is loaded
  const [hydrated, setHydrated] = useState(false);

  // flags/timers
  const startedPollRef = useRef(false);
  const seenPositiveRef = useRef(false);
  const matchPollStopRef = useRef(false);
  const matchPollTimerRef = useRef(null);
  const inflightRef = useRef(null);

  // fetch/navigation locks to prevent self-cancellation races
  const isFetchingMatchRef = useRef(false);
  const isNavigatingRef = useRef(false);

  const socket = useMemo(() => getRevealSocket(), []);

  // Access watchdog via revealSocket (registration/presence)
  useEffect(() => {
    startLocationHeartbeat();
    startRevealAccessWatch(eventId, {
      onDenied: () => navigate(`/events/${eventId}`, { replace: true }),
      intervalMs: 15000,
    });
    return () => {
      stopRevealAccessWatch(eventId);
      stopLocationHeartbeat();
    };
  }, [eventId, navigate]);

  // OPTIONAL: instant socket kick (in addition to the watchdog)
  useEffect(() => {
    const socket = getRevealSocket();
    const onDenied = () => navigate(`/events/${eventId}`, { replace: true });
    socket.on("reveal:accessDenied", onDenied);
    return () => socket.off("reveal:accessDenied", onDenied);
  }, [eventId, navigate]);
// ✅ Authoritative route listener with de-dupe + lock + intro defer
useEffect(() => {
  const s = getRevealSocket();

  // remembers the last route we acted on to avoid loops/bounce
  const lastSigRef = { current: { sig: null, at: 0 } };
  // if a route arrives during intro, we store it and apply after intro finishes
  const deferredRef = { current: null };

  function sameSig(a, b) { return a === b && a !== null; }
  function buildSig(p) {
    return `${p?.route || ''}|${p?.stageIndex ?? ''}|${p?.matchId ?? ''}`;
  }

  function routeLocked() {
    try {
      const until = Number(sessionStorage.getItem(`reveal_route_lock:${eventId}`) || 0);
      return until > Date.now();
    } catch { return false; }
  }

  function setRouteLock(ms = 1200) {
    try { sessionStorage.setItem(`reveal_route_lock:${eventId}`, String(Date.now() + ms)); } catch {}
  }

  async function onRoute(payload) {
    // ignore other events
    if (payload?.eventId && String(payload.eventId) !== String(eventId)) return;

    // 1) short lock to ignore rapid repeats (from hydration sweep)
    if (routeLocked()) return;

    // 2) de-dupe identical instructions for ~2s
    const now = Date.now();
    const sig = buildSig(payload);
    if (sameSig(lastSigRef.current.sig, sig) && (now - lastSigRef.current.at) < 2000) {
      return;
    }

    // 3) during intro/booting/navigating: defer partner route, let the overlay run
    if (
      payload?.route === 'partner' &&
      (introBooting || showIntro || navigating)
    ) {
      deferredRef.current = payload; // apply after intro completes
      return;
    }

    // 4) clear old locks/stickies so we don't bounce
    try {
      sessionStorage.removeItem(`reveal_route_lock:${eventId}`);
      sessionStorage.removeItem(`reveal_partner_sticky_until:${eventId}`);
      sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`);
    } catch {}

    // 5) navigate once; then set a short lock and remember signature
    if (payload?.route) {
      setRouteLock(1200);
      lastSigRef.current = { sig, at: now };

      navigate(`/reveal/${eventId}/${payload.route}`, {
        replace: true,
        state: {
          stage: payload.stageIndex ?? null,
          matchId: payload.matchId ?? null,
          fromSocket: true,
        },
      });
    } else {
      // fallback: don't spam state; only call once in a while
      // (optional) await enforceRevealState({ api, eventId, pageKind: 'portal', navigate });
    }
  }

  // If Stage-2 unlocks, prefer asking the server once (but do not loop)
  async function onStageUnlocked() {
    if (!routeLocked()) {
      setRouteLock(1000);
      try { await enforceRevealState({ api, eventId, pageKind: 'portal', navigate }); } catch {}
    }
  }

  s.on('reveal:route', onRoute);
  s.on('reveal:stage-unlocked', onStageUnlocked);

  // 👉 Apply any deferred route after intro completes
  //    (hook into your existing showIntro/navigating flags)
  const applyDeferredAfterIntro = () => {
    if (!introBooting && !showIntro && !navigating && deferredRef.current) {
      const p = deferredRef.current; deferredRef.current = null;
      onRoute(p);
    }
  };
  // watch your intro flags
  const introWatcher = setInterval(applyDeferredAfterIntro, 200);

  return () => {
    s.off('reveal:route', onRoute);
    s.off('reveal:stage-unlocked', onStageUnlocked);
    clearInterval(introWatcher);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [api, eventId, navigate, introBooting, showIntro, navigating]);

  useEffect(
    () => () => {
      try {
        socket.off("reveal-mode:reveal");
        socket.off("reveal-mode:revealed");
        socket.off("reveal:epoch");
        socket.off("connect");
      } catch {}
      if (matchPollTimerRef.current) {
        clearTimeout(matchPollTimerRef.current);
        matchPollTimerRef.current = null;
      }
      inflightRef.current?.abort?.();
    },
    [socket]
  );

  // Ensure we are in the user_{id} room so unicast reveal lands
  useEffect(() => {
    try {
      const userId = getUid();
      if (userId) socket.emit("joinUserRoom", String(userId));
    } catch (e) {
      console.warn("[RevealPortal] joinUserRoom failed", e?.message);
    }
  }, [socket]);

  // 🔔 Hard epoch-change push from server → reset view (stateless)
  useEffect(() => {
    function onEpoch({ eventId: eid, epoch }) {
      if (String(eid) !== String(eventId)) return;
      setRevealed(false);
      setShowFeedback(false);
      setRoundEndAt(null);
      setCurrentEpoch(epoch);
      startedPollRef.current = false;
      seenPositiveRef.current = false;
      matchPollStopRef.current = true;
      if (matchPollTimerRef.current) {
        clearTimeout(matchPollTimerRef.current);
        matchPollTimerRef.current = null;
      }
      isFetchingMatchRef.current = false;
      isNavigatingRef.current = false;
      try {
        sessionStorage.removeItem(`reveal_partner_sticky_until:${eventId}`);
        sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`);
        sessionStorage.removeItem(`reveal_block_return:${eventId}`);
      } catch {}
      navigate(`/reveal/${eventId}/portal`, { replace: true });
    }
    socket.on("reveal:epoch", onEpoch);
    return () => socket.off("reveal:epoch", onEpoch);
  }, [socket, navigate, eventId]);
  

  
  // Load event (for countdown + epoch + round end)
   // Load event (for countdown + epoch + round end)
   useEffect(() => {
    let timer;
    (async () => {
      const token =
        localStorage.getItem("accessToken") ||
        JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token;
      if (!token) {
        console.warn("[RevealPortal] abort loading event → no token found");
        return;
      }

      const { data } = await axios.get(
        `${api}/events/${eventId}?t=${Date.now()}`,
        authHeaders()
      );
      setEvent(data);
      const epoch = Number(data?.revealEpoch ?? 0);
      setCurrentEpoch(epoch);

      if (data?.revealRoundEndsAt) {
        const endMs = new Date(data.revealRoundEndsAt).getTime();
        setRoundEndAt(Number.isFinite(endMs) ? endMs : null);
      } else {
        setRoundEndAt(null);
        setShowFeedback(false);
      }

      const start = toLocalDateTime(data?.eventDate, data?.startTime12);
      const revealMs = (data?.revealMode?.revealAtMinutes || 0) * 60_000;
      const target = start ? start.getTime() + revealMs : Date.now();

      const tick = () => setUntil(target - Date.now());
      tick();
      timer = setInterval(tick, 500);

      if (target > Date.now()) setShowFeedback(false);

      // ✅ mark hydrated after event and timers are ready
      setHydrated(true);
    })();
    return () => clearInterval(timer);
  }, [api, eventId]);

  // When round end passes (per server), open feedback (only after hydration)
  useEffect(() => {
    if (!hydrated || !roundEndAt) return;

    // If we're already past end, open once (no flicker)
    if (Date.now() >= roundEndAt) {
      setShowFeedback(true);
      return;
    }

    // Otherwise, watch until we pass end
    const id = setInterval(() => {
      if (Date.now() >= roundEndAt) {
        setShowFeedback(true);
        clearInterval(id);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [roundEndAt, hydrated]);

  // If countdown is positive (pre-reveal), ensure feedback is hidden
  useEffect(() => {
    if (until > 0) setShowFeedback(false);
  }, [until]);

  const stopMatchPoll = () => {
    matchPollStopRef.current = true;
    if (matchPollTimerRef.current) {
      clearTimeout(matchPollTimerRef.current);
      matchPollTimerRef.current = null;
    }
    inflightRef.current?.abort?.();
  };

  // Helper: build intro roster (1 or 2 partners) for intro overlay
  async function loadPartnerForIntro(partnerId) {
    try {
      const { data } = await axios.get(`${api}/users/${partnerId}`, authHeaders());
      const age = (() => {
        const dob = data?.dateOfBirth ? new Date(data.dateOfBirth) : null;
        if (!dob || Number.isNaN(dob)) return null;
        const now = new Date();
        let a = now.getFullYear() - dob.getFullYear();
        const m = now.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) a--;
        return a;
      })();
      return {
        firstName: data?.firstName || "",
        lastName : data?.lastName  || "",
        gender   : data?.gender    || "",
        age,
        profilePicUrl: data?.profilePicUrl || "/default-avatar.png",
      };
    } catch {
      return {
        firstName: "Your",
        lastName : "Match",
        gender: "",
        age: null,
        profilePicUrl: "/default-avatar.png",
      };
    }
  }
  async function buildIntroRoster(primaryPartnerId) {
    // always fetch p1 first
    const p1 = await loadPartnerForIntro(primaryPartnerId);
  
    // 0) NEW: read uiHints from the saved preload payload (the /match 200 you already stash)
    try {
      const raw = sessionStorage.getItem("reveal_preload_match");
      if (raw) {
        const payload = JSON.parse(raw);
        const hints = payload?.uiHints || {};
        const partnerIds = Array.isArray(hints.partnerIds) ? hints.partnerIds : [];
        const secondId =
          hints.secondPartnerId ||
          partnerIds.find(id => String(id) !== String(primaryPartnerId)) ||
          null;
  
        if (secondId) {
          const p2 = await loadPartnerForIntro(secondId);
          return [p1, p2];
        }
      }
    } catch {
      // ignore and fall through to your existing fallbacks
    }
  
    // 1) Prefer local preload (your existing logic)
    try {
      const raw = sessionStorage.getItem("reveal_preload_match");
      if (raw) {
        const data = JSON.parse(raw);
        const arr = Array.isArray(data?.matches) ? data.matches : [];
        if (arr.length > 1) {
          const second = arr.find(m => String(m.partnerId) !== String(primaryPartnerId));
          if (second?.partnerId) {
            const p2 = await loadPartnerForIntro(second.partnerId);
            return [p1, p2];
          }
        }
      }
    } catch {}
  
    // 2) Fallback: /state (sometimes includes a staged second match)
    try {
      const st = await axios.get(`${api}/reveal-mode/${eventId}/state?t=${Date.now()}`, authHeaders());
      const sm = Array.isArray(st?.data?.matches) ? st.data.matches : [];
      if (sm.length > 1) {
        const second = sm.find(m => String(m.partnerId) !== String(primaryPartnerId));
        if (second?.partnerId) {
          const p2 = await loadPartnerForIntro(second.partnerId);
          return [p1, p2];
        }
      }
    } catch {}
  
    // 3) Last resort: /match again
    try {
      const mr = await axios.get(`${api}/reveal-mode/${eventId}/match?t=${Date.now()}`, authHeaders());
      const mm = Array.isArray(mr?.data?.matches) ? mr.data.matches : [];
      if (mm.length > 1) {
        const second = mm.find(m => String(m.partnerId) !== String(primaryPartnerId));
        if (second?.partnerId) {
          const p2 = await loadPartnerForIntro(second.partnerId);
          return [p1, p2];
        }
      }
    } catch {}
  
    // single only
    return [p1];
  }
  

  // Fetch /match; navigate (after intro) on authoritative match
  // Fetch /match; navigate (after intro) on authoritative match
  const refetchMatchAndGo = async () => {
    try {
      if (until > 0 || isFetchingMatchRef.current || isNavigatingRef.current) {
        return false;
      }
      isFetchingMatchRef.current = true;
  
      inflightRef.current?.abort?.();
      const controller = new AbortController();
      inflightRef.current = controller;
  
      const res = await axios.get(`${api}/reveal-mode/${eventId}/match`, {
        ...authHeaders(),
        signal: controller.signal,
        validateStatus: (s) => s === 200 || s === 202 || s === 204 || s === 410,
      });
  
      const { data, status, headers } = res;
  
      if (status === 200) {
        const rr = (data?.state && data?.state.kind) ? data.state : null;
        const isUnifiedMatch = rr?.kind === 'match';
        const legacyMatches  = Array.isArray(data?.matches) ? data.matches : null;
  
        // Build a normalized matches array
        const normalized = legacyMatches
          ? legacyMatches
          : (isUnifiedMatch
              ? [{
                  matchId    : rr.matchId,
                  partnerId  : rr.partnerId,
                  verifyEndAt: rr.verifyEndAt,
                  roomId     : rr.roomId,
                }]
              : []);
  
        // ⬅️ NOW it's safe to compute introTotalStages
        const totalStagesHdr = Number(headers?.['x-reveal-totalstages'] ?? 0);
        const introTotalStages =
          Number.isFinite(totalStagesHdr) && totalStagesHdr > 0
            ? totalStagesHdr
            : (normalized.length > 1 ? 2 : 1);
  
        if (isUnifiedMatch || normalized.length) {
          // Filter out expired matches
          const now = Date.now();
          const liveMatches = normalized.filter((m) => {
            const endMs = m?.verifyEndAt ? new Date(m.verifyEndAt).getTime()
                          : (m?.endTime ? new Date(m.endTime).getTime() : null);
            return !(Number.isFinite(endMs) && endMs <= now);
          });
          if (!liveMatches.length) {
            console.log("[Reveal/Portal] match 200 → no live matches, stay portal");
            return false;
          }
  
          const first = liveMatches[0];
  
          if (typeof data.epoch === "number" || typeof rr?.epoch === "number") {
            setCurrentEpoch((data.epoch ?? rr.epoch) ?? null);
          }
          const rEnd = data?.roundEndAt || rr?.verifyEndAt || rr?.endAt || null;
          if (rEnd) {
            const endMs = new Date(rEnd).getTime();
            if (Number.isFinite(endMs)) setRoundEndAt(endMs);
          }
  
          // Stash for Partner page
          savePartnerPreload({
            ...(data || {}),
            state: 'match',
            matches: liveMatches,
            __introTotalStages: introTotalStages, // ← stored for the overlay
          });
  
          setRevealed(true);
          stopMatchPoll();
  
          // Run intro overlay
          setIntroBooting(true);
          (async () => {
            const primaryPartnerId = first.partnerId;
            if (!primaryPartnerId) {
              setIntroBooting(false);
              startedPollRef.current = false;
              return;
            }
            const roster = await buildIntroRoster(primaryPartnerId);
            setIntroPartners(roster);
            setShowIntro(true);
            setIntroBooting(false);
          })();
  
          return true;
        }
      }
  
      if (status === 410 && data?.state === "ended") {
        setShowFeedback(true);
        return false;
      }
  
      return false; // 202/204 → keep polling
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.message === "canceled") {
        return false;
      }
      if (e?.response?.status === 410) {
        setShowFeedback(true);
      }
      return false;
    } finally {
      if (!isNavigatingRef.current) {
        isFetchingMatchRef.current = false;
      }
    }
  };
  

  // ⏱ Start /match polling only when countdown crosses from >0 → <=0
  useEffect(() => {
    if (revealed) return;

    if (until > 0) {
      seenPositiveRef.current = true;
      return;
    }
    if (!seenPositiveRef.current) return;

    if (startedPollRef.current) return;
    startedPollRef.current = true;
    matchPollStopRef.current = false;

    let stopped = false;
    const loop = async () => {
      if (stopped || revealed || matchPollStopRef.current) return;
      const ok = await refetchMatchAndGo();
      if (!ok) matchPollTimerRef.current = setTimeout(loop, 3000);
    };
    loop();

    return () => {
      stopped = true;
      if (matchPollTimerRef.current) {
        clearTimeout(matchPollTimerRef.current);
        matchPollTimerRef.current = null;
      }
      inflightRef.current?.abort?.();
    };
  }, [until, revealed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wake-up triggers: re-check even if tab was inactive
  useEffect(() => {
    const recheck = () => {
      if (!revealed && until <= 0 && !isFetchingMatchRef.current && !isNavigatingRef.current) {
        startedPollRef.current = false;
        refetchMatchAndGo();
      }
    };
    const onVisible = () => document.visibilityState === "visible" && recheck();
    const onFocus = () => recheck();
    const onPageShow = () => recheck();
    const onSocketReconnect = () => recheck();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    socket.on("connect", onSocketReconnect);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
      socket.off("connect", onSocketReconnect);
    };
  }, [socket, revealed, until]); // eslint-disable-line react-hooks/exhaustive-deps

 // Socket: unicast reveal → show intro then navigate (server authoritative)
useEffect(() => {
  function onReveal(payload) {
    if (typeof payload?.epoch === "number") setCurrentEpoch(payload.epoch);

    setRevealed(true);
    if (payload?.roundEndAt) {
      const endMs = new Date(payload.roundEndAt).getTime();
      if (Number.isFinite(endMs)) setRoundEndAt(endMs);
    }
    stopMatchPoll();

    setIntroBooting(true);
    (async () => {
      let introTotalStages = null;

      // 👇 Ensure we have the same 200 "match" payload the partner page expects
      try {
        const res = await axios.get(`${api}/reveal-mode/${eventId}/match`, {
          ...authHeaders(),
          validateStatus: (s) => s === 200 || s === 202 || s === 204,
        });

        if (res.status === 200 && res.data) {
          // Derive stage count from header or matches length
          const hdr = Number(res.headers?.['x-reveal-totalstages'] ?? 0);
          const matchesArr = Array.isArray(res.data?.matches) ? res.data.matches : [];
          introTotalStages =
            (Number.isFinite(hdr) && hdr > 0) ? hdr : (matchesArr.length > 1 ? 2 : 1);

          // Stash payload + stage count for Partner + Intro
          savePartnerPreload({
            ...res.data,
            __introTotalStages: introTotalStages,
          });
        }
      } catch {
        // ignore — we’ll fall back below
      }

      // Fallback if header/request didn’t supply it: try to infer from socket payload
      if (introTotalStages == null) {
        // any of these from your server would imply 2 stages
        const hintedTwo =
          payload?.totalStages >= 2 ||
          payload?.uiStages?.total >= 2 ||
          payload?.partnerHasTwo === true ||
          payload?.hasTwo === true;
        introTotalStages = hintedTwo ? 2 : 1;
      }

      // Build roster: primary partner from payload, try to fetch second
      let roster = null;
      const primaryId = payload?.partnerId || payload?.partner?._id || payload?.partner?._id;
      if (primaryId) {
        roster = await buildIntroRoster(primaryId);
      } else {
        // Last resort: if preload exists, try to take first match’s partner
        try {
          const raw = sessionStorage.getItem("reveal_preload_match");
          const data = raw ? JSON.parse(raw) : null;
          const firstPartner = Array.isArray(data?.matches) && data.matches[0]?.partnerId;
          roster = firstPartner ? await buildIntroRoster(firstPartner) : null;
        } catch {}
      }

      // Ensure introTotalStages is persisted even if roster fallback path ran
      try {
        const raw = sessionStorage.getItem("reveal_preload_match");
        const prev = raw ? JSON.parse(raw) : {};
        if (prev && introTotalStages != null && prev.__introTotalStages == null) {
          prev.__introTotalStages = introTotalStages;
          savePartnerPreload(prev);
        }
      } catch {}

      // Show intro
      if (Array.isArray(roster) && roster.length) {
        setIntroPartners(roster);
      } else if (payload?.partner) {
        // minimal single partner fallback
        setIntroPartners([payload.partner]);
      }
      setShowIntro(true);
      setIntroBooting(false);
    })();
  }

  socket.on("reveal-mode:reveal", onReveal);
  return () => socket.off("reveal-mode:reveal", onReveal);
}, [socket, api, eventId]);

  useEffect(() => {
    const s = getRevealSocket();
    const enter = () => { try { s.emit('revealPortal:enter', { eventId }); } catch {} };
    enter();
    s.on('connect', enter);
    return () => s.off('connect', enter);
  }, [eventId]);

  // Socket: batch revealed → recheck
  useEffect(() => {
    function onBatch() {
      refetchMatchAndGo();
    }
    socket.on("reveal-mode:revealed", onBatch);
    return () => {
      socket.off("reveal-mode:revealed", onBatch);
    };
  }, [socket]); // eslint-disable-line react-hooks/exhaustive-deps

  // Host/promoter derivations (match Mixer portal behavior)
  const promoter = useMemo(() => {
    const p = event?.promoterId;
    return p && typeof p === "object" ? p : null;
  }, [event]);

  const hostName = useMemo(() => {
    return event?.hostName || promoter?.displayName || promoter?.name || "";
  }, [event, promoter]);

  const hostAvatar = useMemo(() => {
    const raw =
      promoter?.avatarUrl ||
      promoter?.profilePicUrl ||
      promoter?.imageUrl ||
      event?.hostAvatar ||
      "";
    if (raw) {
      if (/^https?:\/\//i.test(raw)) return raw;
      const base = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
      const path = raw.startsWith("/") ? raw : `/${raw}`;
      return `${base}${path}`;
    }
    return `https://i.pravatar.cc/80?u=${encodeURIComponent(
      hostName || event?._id || "host"
    )}`;
  }, [promoter, event, hostName]);

  const promoterEmail = useMemo(() => {
    return promoter?.email || event?.promoterEmail || event?.hostEmail || "";
  }, [promoter, event]);

  // Absolute timestamp when reveal should start (ms since epoch)
  const revealTargetMs = useMemo(() => {
    if (!event?.eventDate || !event?.startTime12) return null;
    const start = toLocalDateTime(event.eventDate, event.startTime12);
    const mins = Number(event?.revealMode?.revealAtMinutes || 0);
    return start ? start.getTime() + mins * 60_000 : null;
  }, [event]);

  return (
    <>
      {/* Same structural layout as Mixer portal */}
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1 }}>
          {/* 1) Navbar (Reveal variant) */}
          <RevealPortalNavBar
  eventId={(eventId || event?._id || '').toString()}
  event={event}
  isPortalPage
/>

          {/* INTRO OVERLAY (runs when reveal lands) */}
          {showIntro && (
         <RevealIntroSequence
         partners={introPartners}
         occupy="overlay"
         totalStages={(function () {
           try {
             const raw = sessionStorage.getItem("reveal_preload_match");
             const payload = raw ? JSON.parse(raw) : null;
             return payload?.__introTotalStages || null;
           } catch {
             return null;
           }
         })()}
         onComplete={() => {
           setNavigating(true);
           setShowIntro(false);
           isNavigatingRef.current = true;
       
           let stageLock = null;
           try {
             const raw = sessionStorage.getItem("reveal_preload_match");
             const data = raw ? JSON.parse(raw) : null;
             if (
               data?.state === "match" &&
               Array.isArray(data.matches) &&
               data.matches.length > 1
             ) {
               stageLock = 0; // Stage-0 = Partner 1
             }
           } catch {}
       
           navigate(`/reveal/${eventId}/partner`, {
             replace: true,
             state: { stage: stageLock },
           });
         }}
       />
       
       
          )}

          {/* 2) Pre-reveal countdown card — KEEP MOUNTED during introBooting/overlay/handoff */}
          <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
            {(!revealed || introBooting || showIntro || navigating) && (
              <PreRevealCard
                orientation={event?.revealMode?.orientation}
                firstName={currentFirstName}
                eventName={event?.name || "Event"}
                targetMs={revealTargetMs}
                title="Reveal starts in"
                subtitle="Keep this page open."
                caption="We’ll reveal your match right here."
                onExpire={() => {
                  seenPositiveRef.current = true;
                  setUntil(0);
                  refetchMatchAndGo().then((ok) => {
                    if (!ok && !window.__revealComputeKicked) {
                      window.__revealComputeKicked = true;
                      axios
                        .post(`${api}/reveal-mode/${eventId}/compute`, {}, authHeaders())
                        .catch(() => {})
                        .finally(() => {
                          refetchMatchAndGo();
                          setTimeout(() => refetchMatchAndGo(), 1500);
                        });
                    }
                  });
                }}
                aria-hidden={showIntro} // keeps SR focus on the overlay when it’s visible
              />
            )}
          </div>

          {/* 3) Instructions banner (Reveal variant) */}
          <RevealInstructionsBanner />

          {/* 4) Event details + Host (Reveal variants), same wrappers as Mixer */}
          {event && (
            <>
              <RevealEventDetailsCard event={event} />
              <section
                style={{
                  maxWidth: 680,
                  width: "94%",
                  margin: "12px auto 0",
                  padding: "0 12px",
                }}
              >
                <RevealHostCard
                  hostName={hostName || "Host"}
                  hostAvatar={hostAvatar}
                  promoterEmail={promoterEmail}
                  eventName={event?.name}
                  eventDate={event?.eventDate}
hostCreatedAt={event.promoterId?.hostSince || event.promoterId?.createdAt}
                  idVerified={true}
                />
              </section>
            </>
          )}
        </div>
        <MinimalFooter />
      </div>

      {/* Feedback modal appears after reveal round expires (from server time) */}
      {showFeedback && (
        <EndOfEventFeedbackModal
          open
          eventId={eventId}
          onClose={() => {
            setShowFeedback(false);
          }}
        />
      )}
    </>
  );
}
